/**
 * Sector Selector Component
 *
 * Allows users to select their business sector during registration.
 * Users can choose from existing sectors or create a new one.
 */

import React, { useState, useEffect } from 'react';
import { Search, Plus, Check, Building2, Briefcase, Palette, Code, Camera, Pen, Megaphone, Users } from 'lucide-react';

export interface Sector {
  id: string;
  name: string;
  description: string;
  keywords: string[];
}

interface SectorSelectorProps {
  selectedSectorId: string | null;
  onSelect: (sectorId: string) => void;
  onCreateNew: (name: string, description: string) => Promise<Sector>;
}

// Icon mapping for known sector types
const SECTOR_ICONS: Record<string, React.ComponentType<{ size?: number; className?: string }>> = {
  'marketing': Megaphone,
  'web': Code,
  'graphic': Palette,
  'video': Camera,
  'photo': Camera,
  'copywriting': Pen,
  'content': Pen,
  'social': Users,
  'freelance': Briefcase,
  'agency': Building2,
  'consulting': Briefcase,
  'pr': Users,
  'events': Users,
};

function getSectorIcon(sector: Sector) {
  const name = sector.name.toLowerCase();
  const keywords = sector.keywords.join(' ').toLowerCase();

  for (const [key, Icon] of Object.entries(SECTOR_ICONS)) {
    if (name.includes(key) || keywords.includes(key)) {
      return Icon;
    }
  }

  return Building2;
}

export function SectorSelector({ selectedSectorId, onSelect, onCreateNew }: SectorSelectorProps) {
  const [sectors, setSectors] = useState<Sector[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showNewForm, setShowNewForm] = useState(false);
  const [newSectorName, setNewSectorName] = useState('');
  const [newSectorDescription, setNewSectorDescription] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    loadSectors();
  }, []);

  const loadSectors = async () => {
    try {
      const response = await fetch('/api/memory/sectors');
      const data = await response.json();
      if (data.success) {
        setSectors(data.data);
      }
    } catch (error) {
      console.error('Failed to load sectors:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredSectors = sectors.filter(sector => {
    const query = searchQuery.toLowerCase();
    return (
      sector.name.toLowerCase().includes(query) ||
      sector.description.toLowerCase().includes(query) ||
      sector.keywords.some(k => k.toLowerCase().includes(query))
    );
  });

  const handleCreateNew = async () => {
    if (!newSectorName.trim()) return;

    setCreating(true);
    try {
      const newSector = await onCreateNew(newSectorName.trim(), newSectorDescription.trim());
      setSectors(prev => [...prev, newSector]);
      onSelect(newSector.id);
      setShowNewForm(false);
      setNewSectorName('');
      setNewSectorDescription('');
    } catch (error) {
      console.error('Failed to create sector:', error);
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
        Loading sectors...
      </div>
    );
  }

  return (
    <div>
      {/* Search */}
      <div style={{
        position: 'relative',
        marginBottom: '1rem',
      }}>
        <Search
          size={18}
          style={{
            position: 'absolute',
            left: '0.75rem',
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'var(--text-muted)',
          }}
        />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search sectors..."
          style={{
            width: '100%',
            padding: '0.75rem 0.75rem 0.75rem 2.5rem',
            border: '1px solid var(--border)',
            borderRadius: '0.5rem',
            fontSize: '0.875rem',
            backgroundColor: 'var(--bg-primary)',
            color: 'var(--text-primary)',
          }}
        />
      </div>

      {/* Sector Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '0.75rem',
        maxHeight: '320px',
        overflowY: 'auto',
        marginBottom: '1rem',
      }}>
        {filteredSectors.map(sector => {
          const Icon = getSectorIcon(sector);
          const isSelected = selectedSectorId === sector.id;

          return (
            <button
              key={sector.id}
              onClick={() => onSelect(sector.id)}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                padding: '0.75rem',
                border: `2px solid ${isSelected ? 'var(--primary)' : 'var(--border)'}`,
                borderRadius: '0.5rem',
                backgroundColor: isSelected ? 'var(--primary-bg, rgba(59, 130, 246, 0.1))' : 'transparent',
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'all 0.2s',
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                width: '100%',
              }}>
                <Icon size={18} style={{ color: isSelected ? 'var(--primary)' : 'var(--text-muted)' }} />
                <span style={{
                  flex: 1,
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  color: isSelected ? 'var(--primary)' : 'var(--text-primary)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>
                  {sector.name}
                </span>
                {isSelected && <Check size={16} style={{ color: 'var(--primary)' }} />}
              </div>
              <span style={{
                fontSize: '0.75rem',
                color: 'var(--text-muted)',
                marginTop: '0.25rem',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}>
                {sector.description}
              </span>
            </button>
          );
        })}
      </div>

      {/* No results */}
      {filteredSectors.length === 0 && searchQuery && (
        <div style={{
          textAlign: 'center',
          padding: '1rem',
          color: 'var(--text-muted)',
          fontSize: '0.875rem',
        }}>
          No sectors found for "{searchQuery}"
        </div>
      )}

      {/* Create New Sector */}
      {!showNewForm ? (
        <button
          onClick={() => setShowNewForm(true)}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            width: '100%',
            padding: '0.75rem',
            border: '2px dashed var(--border)',
            borderRadius: '0.5rem',
            backgroundColor: 'transparent',
            cursor: 'pointer',
            color: 'var(--text-muted)',
            fontSize: '0.875rem',
            transition: 'all 0.2s',
          }}
        >
          <Plus size={18} />
          My sector isn't listed - create new
        </button>
      ) : (
        <div style={{
          padding: '1rem',
          border: '2px solid var(--primary)',
          borderRadius: '0.5rem',
          backgroundColor: 'var(--primary-bg, rgba(59, 130, 246, 0.05))',
        }}>
          <h4 style={{
            margin: '0 0 0.75rem',
            fontSize: '0.875rem',
            fontWeight: 600,
            color: 'var(--text-primary)',
          }}>
            Create New Sector
          </h4>

          <input
            type="text"
            value={newSectorName}
            onChange={(e) => setNewSectorName(e.target.value)}
            placeholder="Sector name (e.g., Interior Design)"
            style={{
              width: '100%',
              padding: '0.625rem',
              border: '1px solid var(--border)',
              borderRadius: '0.375rem',
              fontSize: '0.875rem',
              marginBottom: '0.5rem',
              backgroundColor: 'var(--bg-primary)',
            }}
          />

          <textarea
            value={newSectorDescription}
            onChange={(e) => setNewSectorDescription(e.target.value)}
            placeholder="Brief description (optional)"
            rows={2}
            style={{
              width: '100%',
              padding: '0.625rem',
              border: '1px solid var(--border)',
              borderRadius: '0.375rem',
              fontSize: '0.875rem',
              marginBottom: '0.75rem',
              resize: 'none',
              backgroundColor: 'var(--bg-primary)',
            }}
          />

          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={() => {
                setShowNewForm(false);
                setNewSectorName('');
                setNewSectorDescription('');
              }}
              style={{
                flex: 1,
                padding: '0.5rem',
                border: '1px solid var(--border)',
                borderRadius: '0.375rem',
                backgroundColor: 'transparent',
                cursor: 'pointer',
                fontSize: '0.875rem',
                color: 'var(--text-muted)',
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleCreateNew}
              disabled={!newSectorName.trim() || creating}
              style={{
                flex: 1,
                padding: '0.5rem',
                border: 'none',
                borderRadius: '0.375rem',
                backgroundColor: newSectorName.trim() ? 'var(--primary)' : 'var(--border)',
                cursor: newSectorName.trim() ? 'pointer' : 'not-allowed',
                fontSize: '0.875rem',
                color: 'white',
                fontWeight: 500,
              }}
            >
              {creating ? 'Creating...' : 'Create & Select'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default SectorSelector;
